import { EventType } from "@/analytics/analytics-events";
import {
  CreateAnnotationShareLinkEventProperties,
  DeleteAnnotationEventProperties,
  EditAnnotationEventProperties,
  OpenAnnotationDetailsContextMenuEventProperties,
} from "@/analytics/annotation-events-properties";
import { MeasurementValuesField } from "@/components/r3f/renderers/annotations/annotation-renderers/measurement-values-field";
import { useAnnotationPermissions } from "@/hooks/use-annotation-permissions";
import { useDeleteAnnotation } from "@/hooks/use-delete-annotation";
import { useShareAnnotationOrAnalysis } from "@/hooks/use-share-annotation-or-analysis";
import { selectBackgroundTaskForIElement } from "@/store/background-tasks/background-tasks-selector";
import { useAppSelector } from "@/store/store-hooks";
import { MutationTaskType } from "@/utils/background-tasks";
import {
  FaroIconButton,
  FaroMenu,
  FaroMenuItem,
  ThreeDotsIcon,
  red,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  AnnotationStatus,
  ExternalMarkupIElement,
  IElementMarkup,
  isIElementExternalMarkup,
  isIElementMeasurePolygon,
} from "@faro-lotv/ielement-types";
import { selectAncestor } from "@faro-lotv/project-source";
import { GenericUserInfo } from "@faro-lotv/service-wires";
import { Dialog, Divider } from "@mui/material";
import { useRef, useState, type JSX } from "react";
import { EditAnnotation } from "../../annotations/edit-annotation";

export type CardContextMenuProps = {
  /** The annotation attached to this button */
  markup: IElementMarkup | ExternalMarkupIElement;
  /** The current state of the annotation */
  state: AnnotationStatus;
  /** The user assigned to this annotation */
  mainAssignee: GenericUserInfo | undefined;
  /** The due date of the markup */
  dueDate: string | undefined;
};

/** @returns An icon with a submenu containing operations that can be done on a markup */
export function CardContextMenu({
  markup,
  mainAssignee,
  state,
  dueDate,
}: CardContextMenuProps): JSX.Element {
  const { canWriteAnnotations } = useAnnotationPermissions();
  const externalMarkup = isIElementExternalMarkup(markup);

  const backgroundTask = useAppSelector(
    selectBackgroundTaskForIElement(markup.id),
  );

  const isDeleted =
    backgroundTask?.type === "Mutation" &&
    backgroundTask.mutationType === MutationTaskType.DeleteAnnotation;

  const canEdit = canWriteAnnotations && !externalMarkup && !isDeleted;
  const canDelete = canWriteAnnotations && !isDeleted;

  const [isOpen, setIsOpen] = useState(false);

  const button = useRef<HTMLButtonElement>(null);

  const shareAnnotation = useShareAnnotationOrAnalysis(markup.id, false);

  const { removeAnnotation } = useDeleteAnnotation(markup);

  const [isEditing, setIsEditing] = useState(false);

  const measurement = useAppSelector(
    selectAncestor(markup, isIElementMeasurePolygon),
  );

  return (
    <>
      <FaroIconButton
        size="s"
        margin={0}
        ref={button}
        onClick={() => {
          Analytics.track<OpenAnnotationDetailsContextMenuEventProperties>(
            EventType.openAnnotationDetailsContextMenu,
            {
              via: "annotation list",
            },
          );

          setIsOpen(true);
        }}
      >
        <ThreeDotsIcon />
      </FaroIconButton>
      <FaroMenu
        anchorEl={button.current}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        autoFocus={false}
      >
        <FaroMenuItem
          onClick={() => {
            Analytics.track<CreateAnnotationShareLinkEventProperties>(
              EventType.createAnnotationShareLink,
              {
                via: "annotation list",
              },
            );

            shareAnnotation();
            setIsOpen(false);
          }}
          label="Share"
        />
        {!canEdit && <Divider />}
        {canEdit && (
          <div>
            <FaroMenuItem
              onClick={() => {
                Analytics.track<EditAnnotationEventProperties>(
                  EventType.editAnnotation,
                  {
                    via: "annotation list",
                  },
                );

                setIsEditing(true);
                setIsOpen(false);
              }}
              label="Edit"
            />
            <Divider />
          </div>
        )}
        {canDelete && (
          <FaroMenuItem
            onClick={() => {
              Analytics.track<DeleteAnnotationEventProperties>(
                EventType.deleteAnnotation,
                {
                  via: "annotation list",
                },
              );

              removeAnnotation();
              setIsOpen(false);
            }}
            color={red[300]}
            highlightColor={red[300]}
            label="Delete"
          />
        )}
      </FaroMenu>
      {isEditing && !externalMarkup && (
        <Dialog
          open
          slotProps={{
            paper: {
              sx: {
                padding: 0,
                backgroundColor: "transparent",
                overflow: "hidden",
                boxShadow: "none",
              },
            },
          }}
        >
          <EditAnnotation
            iElement={markup}
            title={markup.name}
            description={markup.descr ?? undefined}
            assignee={mainAssignee?.id}
            status={state}
            dueDate={dueDate ? new Date(dueDate) : undefined}
            onClose={() => setIsEditing(false)}
          >
            {measurement && (
              <MeasurementValuesField measurement={measurement} />
            )}
          </EditAnnotation>
        </Dialog>
      )}
    </>
  );
}
